/**
 * Per-tank weapon particle effects — Bouncer, Scatter, Vortex, Gyro.
 */

import type { ParticleCore } from '../types';
import { darken, lighten, warm } from '../color-utils';

// === BOUNCER EFFECTS ===

/**
 * Bank shot impact — directional spark spray + flash + shockwave at bounce point.
 * Reads as "ricochet energy" — the core mechanic made visible.
 */
export function spawnBouncerBankShot(core: ParticleCore, x: number, y: number, color: number, intensity: number): void {
  // Bright flash at impact point
  core.spawnParticle(x, y, 0xffffff, 0, 0.5, 0.04, 0.08, 1.5 * intensity, 2.5 * intensity, 0, 0.3, true, true, 0.9);
  core.spawnParticle(x, y, color, 0, 0.3, 0.05, 0.1, 1.0 * intensity, 1.8 * intensity, 0, 0.4, true, false, 0.7);

  // Directional spark spray — 10 sparks radiating outward
  const sparkCount = Math.round(10 * intensity);
  for (let i = 0; i < sparkCount; i++) {
    const angle = (i / sparkCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
    const speed = 4 + Math.random() * 8;
    const r = Math.random();
    const sparkColor = r < 0.3 ? 0xffffff : r < 0.7 ? color : darken(color, 0.6);
    core.spawnParticleAtAngle(
      x, y, angle, speed * intensity,
      sparkColor,
      0.1 + Math.random() * 0.08,
      0.25 + Math.random() * 0.3,
      0.06, 0.94,
      true, true,
      0.85,
      2 + Math.random() * 2, // stretch — velocity streaks
    );
  }

  // Dark contrast debris (Sakurai: dark framing)
  for (let i = 0; i < 4; i++) {
    const angle = Math.random() * Math.PI * 2;
    core.spawnParticleAtAngle(
      x, y, angle, 1.5 + Math.random() * 3,
      0x222200,
      0.12 + Math.random() * 0.1,
      0.3 + Math.random() * 0.2,
      0.1, 0.93,
      false, true, 0.6,
    );
  }

  // Small shockwave
  core.spawnShockwave(x, y, color, 35 * intensity, 220, 2, 0.12);

  // Impact lines — 4 radial streaks
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
    const life = 0.08 + Math.random() * 0.04;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 3,
      outerRadius: 18 + Math.random() * 10,
      expandSpeed: 180 + Math.random() * 60,
      color: Math.random() < 0.3 ? 0xffffff : color,
      lineWidth: 2 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }
}

// === SCATTER EFFECTS ===

/**
 * Buckshot muzzle — wide-cone fan of sparks on pellet spread fire.
 * Reads as "shotgun blast" — immediate visual identity.
 */
export function spawnScatterBuckshot(core: ParticleCore, x: number, y: number, angle: number, color: number): void {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // Wide-cone spark fan — 16 sparks across 90° arc
  for (let i = 0; i < 16; i++) {
    const spread = (Math.random() - 0.5) * Math.PI * 0.5; // wide 90° cone
    const sparkAngle = angle + spread;
    const speed = 8 + Math.random() * 14;
    const r = Math.random();
    const sparkColor = r < 0.35 ? color : r < 0.6 ? warm(color, 0.5) : r < 0.8 ? 0xffffff : darken(color, 0.5);
    const off = 3 + Math.random() * 6;
    core.spawnParticleAtAngle(
      x + Math.cos(sparkAngle) * off,
      y + Math.sin(sparkAngle) * off,
      sparkAngle, speed,
      sparkColor,
      0.06 + Math.random() * 0.08,
      0.15 + Math.random() * 0.2,
      0.08, 0.93,
      true, true,
      0.8,
      2.5 + Math.random() * 2, // stretch
    );
  }

  // Core flash
  core.spawnParticle(x + cos * 5, y + sin * 5, 0xffffff, 0, 1.0, 0.04, 0.08, 1.5, 2.5, 0, 0.3, true, true, 0.9);
  core.spawnParticle(x + cos * 3, y + sin * 3, color, 0, 0.5, 0.06, 0.1, 1.0, 1.8, 0, 0.4, false, false, 0.7);

  // Small dark puffs (gunpowder smoke)
  for (let i = 0; i < 4; i++) {
    const smokeAngle = angle + Math.PI + (Math.random() - 0.5) * Math.PI * 0.4;
    core.spawnParticleAtAngle(
      x, y, smokeAngle, 1 + Math.random() * 2,
      Math.random() < 0.5 ? 0x332211 : 0x221100,
      0.15 + Math.random() * 0.15,
      0.6 + Math.random() * 0.5,
      -0.01, 0.97,
      false, false, 0.4,
    );
  }

  // Wide shockwave
  core.spawnShockwave(x, y, color, 45, 280, 2.5, 0.1);
}

// === VORTEX EFFECTS ===

/**
 * Void-tear muzzle — dark center + purple shards radiating outward.
 * Reads as "spacetime rip" — the portal origin.
 */
export function spawnVortexVoidTear(core: ParticleCore, x: number, y: number, angle: number, color: number): void {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // Dark void center — NORMAL blend, absorbs light
  for (let i = 0; i < 6; i++) {
    const off = Math.random() * 8;
    core.spawnParticle(
      x + cos * off, y + sin * off,
      0x110022,
      0, 0.5, 0.1, 0.18,
      1.0, 2.0,
      0, 0.4,
      false, false, // NORMAL, soft — dark absorbing center
      0.8,
    );
  }

  // Purple energy shards radiating outward — ADD blend, hard
  for (let i = 0; i < 12; i++) {
    const shardAngle = angle + (Math.random() - 0.5) * Math.PI * 0.6;
    const speed = 6 + Math.random() * 10;
    const r = Math.random();
    const shardColor = r < 0.3 ? 0xffffff : r < 0.6 ? color : r < 0.8 ? lighten(color, 0.3) : 0x6622AA;
    const off = 3 + Math.random() * 5;
    core.spawnParticleAtAngle(
      x + Math.cos(shardAngle) * off,
      y + Math.sin(shardAngle) * off,
      shardAngle, speed,
      shardColor,
      0.08 + Math.random() * 0.08,
      0.2 + Math.random() * 0.25,
      0, 0.93,
      true, true,
      0.85,
      2.5 + Math.random() * 2,
    );
  }

  // Contracting dark shockwave (void pulls inward)
  core.spawnShockwave(x, y, 0x220044, 20, 80, 2, 0.08);
  // Expanding purple ring
  core.spawnShockwave(x, y, color, 40, 250, 2.5, 0.12);

  // Impact lines — short, dark
  for (let i = 0; i < 4; i++) {
    const lineAngle = angle + (Math.random() - 0.5) * 0.8;
    const life = 0.08 + Math.random() * 0.04;
    core.impactLines.push({
      x, y, angle: lineAngle,
      innerRadius: 3,
      outerRadius: 20 + Math.random() * 12,
      expandSpeed: 160 + Math.random() * 60,
      color: Math.random() < 0.3 ? 0xffffff : color,
      lineWidth: 2 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }
}

// === GYRO EFFECTS ===

/**
 * Spin-launch muzzle — circular particle burst (spinning top release).
 * Reads as "spinning energy released" — rotational launch feel.
 */
export function spawnGyroSpinLaunch(core: ParticleCore, x: number, y: number, _angle: number, color: number): void {
  // Circular burst — particles at even angles around the launch point
  const ringCount = 12;
  for (let i = 0; i < ringCount; i++) {
    const ringAngle = (i / ringCount) * Math.PI * 2;
    const speed = 5 + Math.random() * 6;
    const r = Math.random();
    const ringColor = r < 0.35 ? color : r < 0.6 ? lighten(color, 0.3) : r < 0.8 ? 0xffffff : darken(color, 0.4);
    core.spawnParticleAtAngle(
      x, y, ringAngle, speed,
      ringColor,
      0.1 + Math.random() * 0.08,
      0.2 + Math.random() * 0.25,
      0.02, 0.94,
      true, true,
      0.8,
      1.5 + Math.random(), // mild stretch
    );
  }

  // Central spin flash
  core.spawnParticle(x, y, 0xffffff, 0, 0.5, 0.05, 0.08, 1.5, 2.5, 0, 0.3, true, true, 0.9);
  core.spawnParticle(x, y, color, 0, 0.3, 0.06, 0.1, 1.0, 1.8, 0, 0.4, true, false, 0.7);

  // Circular shockwave (rotational energy release)
  core.spawnShockwave(x, y, color, 35, 200, 2, 0.12);
  core.spawnShockwave(x, y, lighten(color, 0.3), 20, 150, 1.5, 0.08);
}
